import { Button, Typography } from '@mui/material';
import { ComponentType, ReactNode } from 'react';
import { Control, FieldErrors } from 'react-hook-form';

import { ActionBar } from '@/components/Layout';

import { UserAccountType } from '@/types/user';

import { getDataTestId } from '@/utils/browser';

import { QuestionProps } from '../QuestionDrawer.interface';
import { QuestionForm } from '../QuestionForm';

import { QuestionsContainerItem } from '../QuestionAboutMultipleMonthlyDeposit.styled';

export type AccountSectionFormProps<TAccountFields extends object> = {
  account: UserAccountType;
  index: number;
  control: Control<{ accounts: TAccountFields[] }>;
  fieldErrors: FieldErrors<{ accounts: TAccountFields[] }>;
  isPending: boolean;
};

type RemediationAccountQuestionFormProps<TAccountFields extends object> = Omit<
  QuestionProps,
  'onAnswered'
> & {
  control: Control<{ accounts: TAccountFields[] }>;
  fieldErrors: FieldErrors<{ accounts: TAccountFields[] }>;
  isValid: boolean;
  isPending: boolean;
  error: Error | null;
  onSubmit: () => void;
  activeAccounts: UserAccountType[];
  submitLabel: string;
  AccountSectionForm: ComponentType<AccountSectionFormProps<TAccountFields>>;
};

export function RemediationAccountQuestionForm<TAccountFields extends object>({
  control,
  fieldErrors,
  isValid,
  isPending,
  error,
  onSubmit,
  activeAccounts,
  submitLabel,
  AccountSectionForm,
  ...questionProps
}: RemediationAccountQuestionFormProps<TAccountFields>) {
  return (
    <QuestionForm
      {...questionProps}
      isLoading={isPending}
      answerError={error}
      onSubmit={onSubmit}
    >
      {activeAccounts.map((account, index) => (
        <AccountSectionLayout key={account.id} account={account}>
          <AccountSectionForm
            {...{ account, index, control, fieldErrors, isPending }}
          />
        </AccountSectionLayout>
      ))}
      <ActionBar>
        <Button
          {...getDataTestId(`basic-info-proceed-button`)}
          variant="outlined"
          fullWidth
          type="submit"
          disabled={!isValid || isPending}
        >
          {submitLabel}
        </Button>
      </ActionBar>
    </QuestionForm>
  );
}

type AccountSectionLayoutProps = {
  account: UserAccountType;
  children: ReactNode;
};
function AccountSectionLayout({
  account,
  children,
}: Readonly<AccountSectionLayoutProps>) {
  const sectionTitle = `${account.name} (${account.currency})`;

  return (
    <QuestionsContainerItem>
      <Typography marginTop={2} marginBottom={2} variant="body1Medium">
        {sectionTitle}
      </Typography>
      {children}
    </QuestionsContainerItem>
  );
}
